import React from "react";

import CheckCircleIcon from "icons/CheckCircle";
import ErrorOutlinedIcon from "icons/ErrorOutlined";
import CloseIcon from "../public/images/icons/close.svg";

export const palette = {
  primary: {
    light: "",
    main: "#8C61FE",
    dark: "#6B42D6",
  },
  secondary: {
    light: "",
    main: "#ff5722",
    dark: "#F05623",
  },
  error: {
    main: "#F36158",
    dark: "#F36158",
  },
  warning: {
    main: "#F39958",
  },
  success: {
    main: "#B0E895",
  },
  background: {
    default: "#0D0D0F",
    paper: "#0D0D0F",
  },
};

export const themeConfig = {
  palette: { mode: "dark", ...palette },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1380,
    },
  },
  typography: {
    fontSize: 12,
    htmlFontSize: 16,
    fontFamily: "Poppins",
    fontWeight: 300,
    h1: {
      fontFamily: "Buduj",
      fontSize: "28px", // convert from rem to px
      letterSpacing: "0",
      lineHeight: "40px",
      fontWeight: 500,
    },
    h2: {
      fontFamily: "Buduj",
      fontSize: "20px", // convert from rem to px
      letterSpacing: "2%",
      lineHeight: "40px",
      fontWeight: 500,
      "& sup": {
        fontSize: "12px",
        marginLeft: "4px",
      },
    },
    h5: {
      fontSize: "14px", //convert from rem to px
      letterSpacing: "0",
      lineHeight: "24px",
      fontWeight: 500,
    },
    subtitle1: {
      fontWeight: 500,
      fontSize: "14px",
      lineHeight: "24px",
    },
    subtitle2: {
      fontWeight: 500,
      fontSize: "12px",
      lineHeight: "24px",
      color: "var(--on-surface-primary)",
    },
    body1: {
      fontWeight: 300,
      fontSize: "14px",
      lineHeight: "24px",
    },
    body2: {
      fontWeight: 300,
      fontSize: "14px",
      letterSpacing: "0",
      color: "var(--on-surface-secondary)",
      lineHeight: "26px",
      "& b": {
        fontWeight: 500,
        color: "var(--on-surface-primary)",
      },
    },
    sxRegular: {
      color: "var(--on-surface-secondary)",
      fontSize: "14px",
      fontStyle: "normal",
      fontWeight: 400,
      lineHeight: "20px",
    },
  },
  components: {
    // MuiChip: {
    //   styleOverrides: {
    //     root: {
    //       border: "1px solid #1D1E23",
    //     },
    //   },
    // },
    MuiTypography: {
      styleOverrides: {
        root: {
          "& a": {
            color: "var(--brand-primary-light)",
          },
          "& p": {
            margin: 0,
          },
          "& b, & strong": {
            fontWeight: 500,
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
          borderBottom: "1px solid #1D1E23",
          boxShadow: "none",
          height: "64px",
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          "&&&": {
            gap: "16px",
            minHeight: "64px",
          },
        },
      },
    },
    MuiButtonGroup: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiFab: {
      styleOverrides: {
        sizeLarge: {
          width: "56px",
          height: "56px",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        sizeLarge: {
          padding: "0 16px",
          height: "48px",
        },
        sizeMedium: {
          padding: "0 16px",
          height: "40px",
        },
        sizeSmall: {
          padding: "0 12px",
          height: "32px",
        },
        contained: {
          "&.MuiButton-containedPrimary": {
            backgroundColor: "#6B42D6",
            ":hover, :focus": {
              backgroundColor: "#8968DE",
            },
            ":active": {
              backgroundColor: "#613CC3",
            },
          },
          ":disabled": {
            backgroundColor: "var(--surface-outlines)",
            color: "var(--on-surface-disabled)",
          },
        },
        outlined: {
          "&.MuiButton-outlinedPrimary": {
            color: "var(--on-surface-primary)",
            borderColor: "var(--surface-outlines)",
            ":hover, :focus": {
              backgroundColor: "transparent",
              borderColor: "#8C61FE",
            },
            ":active": {
              backgroundColor: "transparent",
              borderColor: "var(--brand-primary-light-pressed)",
            },
            "&:disabled, &.Mui-disabled": {
              color: "var(--on-surface-disabled)",
              "& .MuiButton-startIcon, & .MuiButton-endIcon": {
                color: "inherit",
              },
            },
            "& .MuiButton-startIcon, & .MuiButton-endIcon": {
              color: palette.primary.main,
            },
          },
        },
        text: {
          color: "var(--on-surface-primary)",
          ":hover, :focus, :active": {
            backgroundColor: "transparent",
            color: "#8C61FE",
          },
          ":disabled": {
            color: "var(--on-surface-disabled)",
          },
          "&.MuiButtonGroup-grouped:not(:last-of-type)": {
            // FIXME
            borderRight: "none !important",
          },
        },
        root: {
          lineHeight: "24px",
          borderRadius: "100px",
          fontSize: "14px",
          textTransform: "initial",
          flexShrink: 0,
          minWidth: "auto",
          ":disabled": {
            borderColor: "var(--surface-outlines)",
            color: "var(--on-surface-disabled)",
          },
          "& .MuiCircularProgress-root": {
            border: "none",
            backgroundColor: "transparent",
          },
        },
      },
      variants: [
        {
          props: { variant: "underlined" },
          style: {
            textDecoration: "underline",
            color: palette.primary.main,
            padding: 0,
            height: 24,
            backgroundColor: "none",
            ":hover, :focus, :active": {
              backgroundColor: "transparent",
            },
          },
        },
        {
          props: { variant: "dropdown" },
          style: {
            fontWeight: 300,
            color: "var(--on-surface-primary)",
            border: "1px solid var(--surface-outlines)",
            transition: "0.2s linear",
            ":hover, :focus, :active": {
              backgroundColor: "transparent",
              borderColor: "var(--surface-outlines)",
              color: palette.primary.main,
            },
            ":disabled": {
              color: "var(--on-surface-disabled)",
            },
            "& .MuiButton-endIcon": {
              fontSize: "20px",
            },
          },
        },
        {
          props: { variant: "dropdown-contained" },
          style: {
            fontWeight: 500,
            fontSize: "12px",
            color: "var(--brand-primary-light)",
            border: "none",
            background: "rgba(var(--brand-primary-light-rgb), 0.2)",
            transition: "0.2s linear",
            padding: "0 4px 0 8px",
            ":disabled": {
              color: "var(--on-surface-disabled)",
              background: "rgba(var(--on-surface-disabled-rgb), 0.2)",
            },
            "& .MuiButton-endIcon": {
              fontSize: "20px",
              marginLeft: "4px",
            },
          },
        },
        {
          props: { variant: "contained-gradient" },
          style: {
            fontWeight: 500,
            color: "var(--on-surface-primary)",
            background:
              "linear-gradient(90deg, var(--brand-primary-dark) 0.34%, var(--brand-secondary-light) 193.81%), var(--brand-primary-dark)",
            ":disabled": {
              color: "var(--on-surface-disabled)",
              background: "initial",
            },
            "& .MuiButton-endIcon": {
              fontSize: "20px",
            },
            "& .MuiCircularProgress-root": {
              color: "var(--on-surface-primary)",
            },
          },
        },
        {
          props: { variant: "outlined-gradient" },
          style: {
            fontWeight: 500,
            color: "var(--on-surface-primary)",
            background:
              "linear-gradient(var(--surface-elements-elevation-low), var(--surface-elements-elevation-low)) padding-box, linear-gradient(90deg, var(--brand-primary-dark) 0.34%, var(--brand-secondary-light) 193.81%), var(--brand-primary-dark)",
            ":disabled": {
              color: "var(--on-surface-disabled)",
              background: "initial",
            },
            ":hover, :focus, :active": {
              background:
                "linear-gradient(90deg, var(--brand-primary-dark) 0.34%, var(--brand-secondary-light) 193.81%), var(--brand-primary-dark)",
            },
            border: "solid 1px transparent",
            "& .MuiButton-endIcon": {
              fontSize: "20px",
            },
            "& .MuiCircularProgress-root": {
              color: "var(--on-surface-primary)",
            },
          },
        },
      ],
    },
    MuiIconButton: {
      styleOverrides: {
        sizeMedium: {
          width: "40px",
          height: "40px",
        },
        sizeSmall: {
          width: "32px",
          height: "32px",
        },
        root: {
          "&:hover": {
            color: "var(--brand-primary-light-hovered)",
          },
          "&:active": {
            color: palette.primary.main,
          },
          "&:disabled": {
            color: "var(--on-surface-disabled)",
          },
        },
      },
      variants: [
        {
          // FIXME write some file.d.ts with types to fix vscode liner error
          props: { variant: "outlined" },
          style: {
            border: "1px solid var(--surface-outlines)",
            background: "var(--surface-elements-elevation-low)",
          },
        },
        {
          // FIXME write some file.d.ts with types to fix vscode liner error
          props: { variant: "contained" },
          style: {
            border: "1px solid var(--brand-primary-dark)",
            background: "var(--brand-primary-dark)",
            "&:hover": {
              background: "var(--brand-primary-dark-hovered)",
            },
          },
        },
      ],
    },
    MuiToggleButton: {
      styleOverrides: {
        sizeMedium: {
          width: "40px",
          height: "40px",
        },
        sizeSmall: {
          width: "32px",
          height: "32px",
        },
        root: {
          borderRadius: "100px",
          borderColor: "var(--surface-outlines)",
          background: "var(--surface-elements-elevation-low)",
          "&:hover": {
            color: "var(--brand-primary-light-hovered)",
            background: "initial",
          },
          "&:active": {
            color: palette.primary.main,
          },
          "&:disabled": {
            color: "var(--on-surface-disabled)",
          },
          "&.Mui-selected": {
            color: palette.primary.main,
            background: "var(--surface-elements-elevation-low)",
            "&:hover": {
              background: "initial",
            },
          },
          "&.MuiToggleButtonGroup-grouped:not(:first-of-type)": {
            borderColor: "var(--surface-outlines)",
          },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          backgroundColor: "var(--surface-outlines)",
          "& .MuiLinearProgress-bar": {
            borderRadius: 20,
          },
        },
      },
    },
    MuiCircularProgress: {
      defaultProps: {
        size: 32,
      },
      styleOverrides: {
        root: {
          backgroundColor: "var(--surface-elements-elevation-low)",
          borderRadius: "50%",
          border: "1px solid var(--surface-outlines)",
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: "16px",
          border: "1px solid var(--surface-outlines)",
          backgroundColor: "var(--surface-elements-elevation-high)",
          backgroundImage: "none",
          marginTop: "8px",
          minWidth: "200px",
        },
        root: {},
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          padding: "10px 12px",
          "& .MuiListItemText-root": {
            whiteSpace: "normal",
          },
          "&.Mui-selected": {
            backgroundColor: "transparent",
            "& .MuiListItemText-root + .MuiListItemIcon-root": {
              minWidth: "auto",
              marginLeft: "16px",
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        sizeMedium: {
          height: "40px",
          "& .MuiChip-label": {
            padding: "0 16px",
          },
          "& .MuiChip-icon": {
            marginLeft: "16px",
          },
          "& .MuiAvatar-root": {
            width: "32px",
            height: "32px",
            marginLeft: "4px",
            backgroundColor: "#141417",
          },
        },
        sizeSmall: {
          height: "32px",
          "& .MuiChip-label": {
            padding: "0 12px",
          },
          "& .MuiChip-icon": {
            marginLeft: "12px",
          },
          "& .MuiAvatar-root": {
            width: "24px",
            height: "24px",
            marginLeft: "4px",
            backgroundColor: "#141417",
          },
        },
        root: {
          fontWeight: "500",
          fontSize: "14px",
          borderRadius: "100px",
          "&.MuiChip-colorDefault": {
            borderColor: "var(--surface-outlines)",
            backgroundColor: "var(--surface-elements-elevation-low)",
          },
          "& .MuiAvatar-root": {
            borderRadius: "100px",
            border: "1px solid var(--surface-outlines)",
            backgroundColor: "var(--surface-elements-elevation-low)",
            color: "var(--on-surface-secondary)",
            fontWeight: 300,
            fontSize: "14px",
            padding: "2px",
          },
          "& .MuiAvatar-root + .MuiAvatar-root": {
            marginLeft: "-8px",
          },
        },
      },
      variants: [
        {
          // FIXME write some file.d.ts with types to fix vscode liner error
          props: { variant: "outlined-icon-only" },
          style: {
            "& .MuiChip-label": {
              display: "none",
            },
            "& .MuiSvgIcon-root": {
              margin: 0,
              color: "inherit",
            },
            border: "1px solid var(--surface-outlines)",
            width: "40px",
            "&.MuiChip-sizeSmall": {
              width: "32px",
            },
          },
        },
        {
          // FIXME write some file.d.ts with types to fix vscode linter error
          props: { variant: "outlined-gradient" },
          style: {
            "&&&": {
              border: "none",
              position: "relative",
              "&:before": {
                content: '""',
                position: "absolute",
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                zIndex: -1,
                margin: "-1px",
                borderRadius: "inherit",
                background:
                  "linear-gradient(to right, var(--brand-primary-dark), var(--brand-secondary-light))",
              },
            },
          },
        },
        {
          props: { variant: "transparent" },
          style: {
            "&.MuiChip-colorDefault": {
              border: "1px solid var(--surface-outlines)",
              backgroundColor: "transparent",
            },
            color: "var(--on-surface-secondary)",
            fontWeight: 300,
          },
        },
        {
          props: { variant: "transparent-outlined" },
          style: {
            "&.MuiChip-colorDefault": {
              border: "1px solid var(--surface-outlines)",
              backgroundColor: "transparent",
              color: "var(--on-surface-primary)",
            },
            fontWeight: 300,
          },
        },
      ],
    },
    MuiInputBase: {
      styleOverrides: {
        sizeMedium: {
          height: "40px",
        },
        sizeSmall: {
          height: "32px",
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          caretColor: palette.primary.main,
          "& .MuiOutlinedInput-root": {
            borderRadius: "100px",
            backgroundColor: "var(--surface-elements-elevation-low)",
            "& .MuiInputBase-input": {
              textAlign: "inherit",
              padding: "0 15px",
              height: "40px",
              "&.MuiInputBase-sizeSmall": {
                height: "32px",
              },
            },
            "& .MuiInputBase-adornedStart": {
              paddingLeft: "16px",
              "&.MuiInputBase-sizeSmall": {
                paddingLeft: "12px",
              },
            },
            "& .MuiButtonBase-root.MuiAutocomplete-clearIndicator": {
              backgroundColor: "transparent",
            },
          },
          "& .MuiInputBase-multiline": {
            borderRadius: "16px",
            "& .MuiInputBase-input": {
              padding: "0",
            },
          },
          "& .Mui-focused": {
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "var(--surface-outlines)",
              borderWidth: "1px",
            },
            "& .MuiSvgIcon-root": {
              color: palette.primary.main,
            },
            "& ::placeholder": {
              color: "var(--on-surface-disabled)",
            },
          },
          ".MuiOutlinedInput-notchedOutline, &:hover .MuiOutlinedInput-notchedOutline":
            {
              borderColor: "var(--surface-outlines)",
              borderWidth: "1px",
            },
          "& .MuiInputBase-adornedStart": {
            gap: "10px",
          },
          "& .MuiInput-underline": {
            "&:before, &:after": {
              display: "none",
            },
          },
          "& .MuiFormLabel-root": {
            color: "#fff",
            marginBottom: "8px",
            fontWeight: "500",
            fontSize: "14px",
            lineHeight: "24px",
            transform: "none",
            position: "relative",
          },
          "& legend": { display: "none" },
          "& fieldset": { top: 0 },
          "& ::placeholder": {
            fontWeight: "300",
            color: "var(--on-surface-secondary)",
          },
          "&&&": {
            "& :autofill": {
              "-webkit-box-shadow":
                "0 0 0 100px var(--surface-elements-elevation-low) inset",
              "-webkit-text-fill-color": "#fff",
              borderRadius: "0",
              // fyi removes yellow autofill highlight on safari
              backgroundClip: `content-box !important`,
            },
          },
          "& input[type='search']::-webkit-search-cancel-button": {
            "-webkit-appearance": "none",
            height: 16,
            width: 16,
            backgroundImage: `url(${CloseIcon})`,
            marginRight: "8px",
          },
        },
      },
    },
    MuiDivider: {
      defaultProps: {
        "aria-hidden": true,
      },
      styleOverrides: {
        root: {
          borderColor: "var(--surface-outlines)",
        },
        vertical: {
          height: "12px",
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          "& .MuiInputBase-root": {
            padding: "0",
            fontSize: "14px",
            fontWeight: 300,
          },
        },
        input: {
          boxSizing: "border-box",
        },
        endAdornment: {
          right: "16px",
          top: 0,
        },
        popupIndicator: {
          display: "none",
        },
        popper: {
          // FIXME
          top: "8px !important",
          backgroundColor: "var(--surface-elements-elevation-high)",
          border: "1px solid var(--surface-outlines)",
          borderRadius: "16px",
          overflow: "hidden",
        },
        listbox: {
          backgroundColor: "var(--surface-elements-elevation-high)",
          fontSize: "14px",
          fontWeight: 300,
          padding: "8px 4px",
          "& .MuiListItem-root": {
            boxSizing: "border-box",
            minHeight: "44px",
            padding: "0",
            borderRadius: 12,
            border: "4px solid var(--surface-elements-elevation-high)",
            // FIXME
            "&:hover, &:active, &:focus, &.Mui-focused": {
              backgroundColor: "var(--surface-outlines) !important",
            },
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: "14px",
          maxWidth: "280px",
          backgroundColor: "var(--surface-elements-elevation-high)",
          color: "var(--on-surface-secondary)",
          fontWeight: 300,
          // border: "1px solid var(--surface-outlines)",
          // borderRadius: "16px",
          padding: "12px 16px",
          margin: "0 !important",
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        // FIXME
        root: {
          // border: "1px solid var(--surface-outlines)",
          borderRadius: "100px",
          backgroundColor: "var(--surface-elements-elevation-low)",
          fontSize: "14px",
          minWidth: "50px",
          height: "40px",
          transition: "0.2s linear",
          "& .MuiSelect-icon": {
            color: "inherit",
            fontSize: "20px",
          },
          "&.MuiInputBase-sizeSmall": {
            height: "32px",
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "var(--surface-outlines)",
          },
          "&:hover, &:active, &:focus, &.Mui-focused": {
            color: "var(--brand-primary-light-hovered)",
            "&:focus, &:active": {
              color: "var(--brand-primary-light)",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "var(--surface-outlines)",
              borderWidth: 1,
            },
          },
          // "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          //   borderColor: "var(--surface-outlines)",
          //   borderWidth: 1,
          // },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        root: {
          "& .MuiPaper-root": {
            borderRadius: "24px",
            borderColor: "var(--surface-outlines)",
            borderWidth: 1,
            backgroundColor: "var(--surface-elements-elevation-high)",
            backgroundImage: "none",
            margin: 0,
            zIndex: 0,
          },
          "& .MuiDialogTitle-root": {
            color: "#fff",
            // fontFamily: "Buduj Sans TRIAL",
            fontStyle: "normal",
            fontWeight: 500,
            fontSize: "17px",
            lineHeight: "40px",
          },
          "& .MuiDialogContent-dividers": {
            borderBottom: "none",
          },
          "& .MuiDialogContent-root": {
            minWidth: "400px",
            color: "var(--on-surface-secondary)",
            fontWeight: 300,
            "& h2, & b, & strong": {
              color: "var(--on-surface-primary)",
            },
            "& ul": {
              margin: 0,
              padding: "0 0 0 1rem",
            },
            borderTop: "none",
          },
          "& .MuiDialogTitle-root + .MuiDialogContent-root": {
            borderTop: "1px solid rgba(255, 255, 255, 0.12)",
          },
          "& .MuiDialogActions-root": {
            padding: "24px",
          },
          "&.bottom .MuiDialog-container": {
            alignItems: "flex-end",
            paddingBottom: "16px",
          },
        },
      },
    },
    MuiAlert: {
      defaultProps: {
        iconMapping: {
          success: <CheckCircleIcon fontSize="small" />,
          warning: <ErrorOutlinedIcon fontSize="small" />,
          error: <ErrorOutlinedIcon fontSize="small" />,
        },
      },
      styleOverrides: {
        root: {
          padding: "12px 16px",
          borderRadius: "16px",
          border: "1px solid var(--surface-outlines)",
          backgroundColor: "var(--surface-elements-elevation-low)",
          minWidth: "480px",
          alignItems: "center",
        },
        icon: {
          padding: 0,
          marginRight: "16px",
          borderRadius: "100px",
          border: "1px solid var(--surface-outlines)",
          backgroundColor: "var(--surface-elements-elevation-high)",
          width: "32px",
          height: "32px",
          justifyContent: "center",
          alignItems: "center",
          flexShrink: 0,
        },
        message: {
          lineHeight: "24px",
          fontWeight: 500,
          padding: 0,
          color: "var(--on-surface-primary)",
          flexGrow: 1,
        },
        action: {
          padding: 0,
          marginLeft: "8px",
          display: "flex",
          borderRadius: "100px",
          border: "1px solid var(--surface-outlines)",
          backgroundColor: "var(--surface-elements-elevation-high)",
          width: "32px",
          height: "32px",
          justifyContent: "center",
          alignItems: "center",
          color: "var(--on-surface-primary)",
          "&&&": {
            marginRight: 0,
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: "var(--on-surface-primary)",
        },
      },
    },
    MuiSwitch: {
      defaultProps: {
        disableRipple: true,
      },
      styledOverrides: {
        switchBase: {
          // Controls default (unchecked) color for the thumb
          color: "transparent",
          border: "1px solid #555866",
          padding: 0,
          margin: 2,
          transitionDuration: "300ms",
        },
        "&.Mui-checked": {
          transform: "translateX(16px)",
          color: "#fff",
          "& + .MuiSwitch-track": {
            backgroundColor: "#65C466",
            opacity: 1,
            border: 0,
          },
          "&.Mui-disabled + .MuiSwitch-track": {
            opacity: 0.5,
          },
        },
        "&.Mui-focusVisible .MuiSwitch-thumb": {
          color: "#33cf4d",
          border: "6px solid #fff",
        },
        "&.Mui-disabled .MuiSwitch-thumb": {
          color: "black",
        },
        "&.Mui-disabled + .MuiSwitch-track": {
          opacity: 0.7,
        },
      },
      "& .MuiSwitch-thumb": {
        boxSizing: "border-box",
        width: 22,
        height: 22,
      },
      track: {
        "& .MuiSwitch-track": {
          borderRadius: 26 / 2,
          backgroundColor: "#39393D",
          opacity: 1,
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        rail: {
          backgroundColor: "var(--surface-outlines)",
          opacity: 1,
        },
        thumb: {
          border: "2px solid var(--on-surface-primary)",
        },
        root: {
          color: palette.primary.dark,
          "&.Mui-disabled.imitateEnabled": {
            color: palette.primary.main,
          },
          "&.MuiSlider-marked": {
            marginBottom: 0,
          },
          "&.gradient": {
            ".MuiSlider-track, .MuiSlider-thumb": {
              background:
                "linear-gradient(90deg, var(--brand-primary-dark) 0.34%, var(--brand-secondary-light) 193.81%), var(--brand-primary-dark)",
            },
          },
          ".MuiSlider-mark": {
            display: "none",
          },
          ".MuiSlider-markLabel": {
            fontSize: "12px",
            letterSpacing: "0px",
            color: "var(--on-surface-secondary)",
            lineHeight: "26px",
            fontFamily: "Poppins",
            overflow: "hidden",
            textoverflow: "ellipsis",
            whiteSpace: "nowrap",
            "&:nth-child(1 of .MuiSlider-markLabel)": {
              transform: "translateX(0%)",
            },
            "&:nth-last-child(1 of .MuiSlider-markLabel)": {
              transform: "translateX(-100%)",
            },
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: "var(--on-surface-secondary)",
          "&:not(.Mui-checked) + .MuiFormControlLabel-label": {
            color: "var(--on-surface-secondary)",
          },
          "&:hover": {
            backgroundColor: "transparent",
          },
        },
      },
    },
    MuiPopper: {
      styleOverrides: {
        root: {
          borderRadius: "16px",
          border: "1px solid var(--surface-outlines)",
          backgroundColor: "var(--surface-elements-elevation-high)",
          minWidth: "200px",
          overflow: "hidden",
          "&.MuiTooltip-popper": {
            minWidth: "auto",
          },
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          backgroundColor: "var(--surface-elements-elevation-low)",
          borderCollapse: "separate",
          borderRadius: "16px",
          border: "1px solid var(--surface-outlines)",
          "td:first-child, th:first-child": {
            borderLeft: "none",
          },
          "td, th ": {
            borderBottom: "none",
          },
          th: {
            borderTop: "none",
            overflow: "hidden",
            position: "sticky",
            top: "70px",
            zIndex: 3,
            background: "var(--surface-elements-elevation-low)",
          },
          "th:first-child": {
            borderRadius: "16px 0 0 0",
          },
          "th:last-child": {
            borderRadius: " 0 16px 0 0",
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderLeft: "1px solid var(--surface-outlines)",
          borderTop: "1px solid var(--surface-outlines)",
          fontWeight: 300,
          padding: "14px 18px",
          color: "var(--on-surface-secondary)",
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          fontWeight: 500,
        },
      },
      variants: [
        {
          props: { variant: "error" },
          style: {
            color: `${palette.error.main} !important`,
            fontWeight: 300,
            textDecoration: "underline",
          },
        },
      ],
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: `1px solid var(--surface-outlines)`,
          borderRadius: "24px",
          backgroundColor: "var(--surface-elements-elevation-low)",
          backgroundImage: "none",
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          height: "48px",
          padding: "0 16px",
          borderBottom: "1px solid var(--surface-outlines)",
          "& .MuiCardHeader-action": {
            alignSelf: "center",
            "& .MuiIconButton-root": {
              color: "var(--on-surface-secondary)",
            },
          },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: "12px 16px",
        },
      },
    },
    MuiCardActions: {
      styleOverrides: {
        root: {
          padding: "0 16px 16px 16px",
        },
      },
    },
  },
};
